import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Linkedin } from 'lucide-react';

const LOGOS = [
  { name: 'Shiprocket', src: '/lovable-uploads/Shiprocket.png' },
  { name: 'Paytm', src: '/lovable-uploads/Paytm.png' },
  { name: 'Sprinto', src: '/lovable-uploads/Sprinto.png' },
  { name: 'Averlon', src: '/lovable-uploads/Averlon.png' },
  { name: 'Fractal', src: '/lovable-uploads/Fractal.png' },
  { name: 'Callhub', src: '/lovable-uploads/Callhub.png' },
  { name: 'StepSecurity', src: '/lovable-uploads/StepSecurity.png' },
];

const BackgroundLogo = ({ logo, index }: { logo: typeof LOGOS[0], index: number }) => {
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const updateCoords = () => {
      setCoords({
        x: Math.random() * 80 + 10, // 10% to 90%
        y: Math.random() * 80 + 10, // 10% to 90%
      });
    };

    const cycle = async () => {
      // Initial delay based on index for stagger
      await new Promise(resolve => setTimeout(resolve, index * 1000));
      
      while (true) {
        updateCoords();
        setIsVisible(true);
        await new Promise(resolve => setTimeout(resolve, 3000));
        setIsVisible(false);
        await new Promise(resolve => setTimeout(resolve, 2000 + Math.random() * 3000));
      }
    };

    cycle();
  }, [index]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5, y: 10 }}
          animate={{ opacity: 0.15, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: -10 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="absolute pointer-events-none grayscale select-none"
          style={{ 
            left: `${coords.x}%`, 
            top: `${coords.y}%`,
          }}
        >
          <img 
            src={logo.src} 
            alt={logo.name} 
            className="h-12 md:h-16 object-contain"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ShoppinTestimonial = () => {
  return (
    <section className="py-24 bg-brand-offwhite relative overflow-hidden">
      {/* Animated Background Logos */}
      <div className="absolute inset-0 z-0">
        {LOGOS.map((logo, idx) => (
          <BackgroundLogo key={`${logo.name}-${idx}`} logo={logo} index={idx} />
        ))}
      </div>

      <div className="container px-4 mx-auto relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Loved by 5000+ founders
          </h2>
        </div>
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/80 backdrop-blur-sm rounded-[32px] shadow-medium border border-gray-100 p-8 md:p-16 text-center"
          >
            <div className="flex flex-col items-center">
              {/* Shoppin' Logo */}
              <div className="mb-8">
                <img 
                  src="/lovable-uploads/shoppin.png" 
                  alt="Shoppin Logo" 
                  className="h-16 md:h-20 object-contain mx-auto"
                />
              </div>

              {/* CEO Info */}
              <div className="mb-10 flex flex-col items-center">
                <h4 className="text-xl md:text-2xl font-bold text-gray-900">Shlok Bhartiya</h4>
                <p className="text-cs-primary font-medium mb-3">CEO at Shoppin'</p>
                
                <a 
                  href="https://www.linkedin.com/in/shlok-bhartiya" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#0077B5] hover:text-[#0077B5]/80 transition-all hover:scale-110 active:scale-95"
                  aria-label="Shlok Bhartiya LinkedIn"
                >
                  <Linkedin className="w-6 h-6 fill-current" />
                </a>
              </div>

              {/* Quote */}
              <div className="relative">
                <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-6xl text-cs-primary/10 font-serif">"</span>
                <div className="text-lg md:text-2xl text-gray-700 leading-relaxed font-medium space-y-6 max-w-3xl mx-auto">
                  <p>
                    If you're an Indian startup struggling to hire quality talent - I get it.
                  </p>
                  <p>
                    What has genuinely helped us scale our product at shoppin' is Cutshort.
                  </p>
                  <p>
                    It is more than just a hiring platform. In last 1 year, we have hired 5 awesome team members and I’ve rarely come across a team so understanding, accommodating, and prompt.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ShoppinTestimonial;
